import StateColorizer from "@/components/live/instance/StateColorizer";
import {t} from "@/i18n";

/**
 * Um fluxo de trabalho: o conjunto de situações por que um item de uma coleção passa.
 *
 * A situação é, ponto a ponto, o que uma opção de domínio é — id vindo de uma sequência no
 * servidor, nome livre, alias fixo, cor-base do Vuetify, e a ORDEM do array valendo como posição.
 * O item guarda o id (`_state`), e é por isso que renomear e reordenar não mexem no dado gravado;
 * o alias é o que as fórmulas citam (`state == "pronto"`).
 */
type Workflow = {
    alias: string
    name: string
    states: State[]
}

export type State = {
    /** Interno, nunca exposto, e atribuído pelo SERVIDOR — como o id de uma opção. */
    id: number
    name: string
    /** O que as fórmulas citam, fixo depois de gravado — como o da opção (`DomainOption.alias`). */
    alias: string
    /**
     * Nome-base de cor do Vuetify quando foi ESCOLHIDA. Ausente significa automática, e aí
     * quem decide é o StateColorizer, pela ordem.
     */
    color?: string
    initial?: boolean
    final?: boolean
}

/** A situação ainda sem id, que é como ela vai ao servidor quando é nova. */
export type StateDraft = Omit<State, 'id'> & { id?: number }

export function emptyWorkflow(): Workflow {
    return {alias: "", name: "", states: []}
}

/**
 * A semente de quem está criando um fluxo: um começo, um meio e um fim.
 *
 * Sem cor fixada: as três nascem automáticas, e quem quiser escolher escolhe. Sem id
 * também — ele nasce no servidor, e é o que o editor manda em branco.
 *
 * Mora aqui e não na tela porque criar fluxo tem par página/modal — a página de criar e o
 * WorkflowDialog —, e os dois precisam partir do mesmo ponto.
 *
 * Função, e nunca uma constante de módulo: `t()` no carregamento congelaria o idioma, e um
 * objeto compartilhado seria editado por quem recebesse a semente.
 */
export function defaultWorkflow(): Workflow {
    return {
        name: t('workflow-default-name'),
        alias: "workflow",
        states: [
            {name: t('state-default-todo'), initial: true},
            {name: t('state-default-doing')},
            {name: t('state-default-done'), final: true}
        ] as State[]
    }
}

/**
 * Tudo aqui lê `this.states`, nunca o parâmetro.
 *
 * Antes os getters fechavam sobre o objeto recebido, e como a atualização de um fluxo grava
 * as mudanças no objeto JÁ enhanced (que está no workspace), `states` mudava mas
 * `initialState`, `stateOf` e as cores continuavam lendo os estados velhos para sempre —
 * editar um fluxo e voltar para a coleção mostrava os cards novos com as cores antigas.
 *
 * O colorizer também não pode ser criado uma vez só: a cor automática depende da ordem, então
 * reordenar ou remover exige recalcular.
 */
export function enhanceWorkflow(workflow: Workflow) {
    return {
        ...workflow,
        get initialState(): State {
            return this.states.find((state: State) => state.initial)!
        },

        get finalStates(): State[] {
            return this.states.filter((state: State) => state.final)
        },

        get nonFinalStates(): State[] {
            return this.states.filter((state: State) => !state.final)
        },

        stateOf(stateId: number | undefined): State | undefined {
            return this.states.find((state: State) => state.id === stateId)
        },

        /**
         * A situação de um item, caindo na INICIAL quando ele não tem nenhuma.
         *
         * Item nasce sem `_state`, e ausência significa "no começo do fluxo" — regra que
         * estava escrita em quatro lugares diferentes (a ficha, a listagem, o Kanban e o
         * consistantInstance) e agora mora só aqui.
         */
        currentStateOf(instance: { _state?: number }): State {
            return this.stateOf(instance._state) || this.initialState
        },

        /** O item terminou? É a pergunta que a listagem e a ficha faziam de cinco formas. */
        isFinal(instance: { _state?: number }): boolean {
            return !!this.currentStateOf(instance).final
        },

        /** A posição no fluxo, que é a ordem que a listagem usa. Ausente vai para o fim. */
        positionOf(stateId: number | undefined): number {
            const index = this.states.findIndex((state: State) => state.id === stateId)
            return index < 0 ? Number.MAX_SAFE_INTEGER : index
        },

        /**
         * A cor da situação: a escolhida, quando há; a derivada da ordem, quando não.
         *
         * Reconstrói o colorizer a cada chamada pelo motivo do comentário de cima — e porque
         * a cor automática de uma situação depende das outras: fixar uma tira aquela cor das
         * disponíveis para as demais.
         */
        colorOf(state: State): string {
            return this.colorOfState(state.id)
        },

        /**
         * A cor pelo ID, que é o que se tem em mão ao desenhar a situação de um item — o chip
         * de um relacionamento, por exemplo. Era um mapa de colorizadores montado igual em
         * duas telas; agora quem pergunta é quem tem o fluxo.
         */
        colorOfState(stateId: number | undefined): string {
            return new StateColorizer(this.states).color(stateId)
        },

        /**
         * As situações com a cor de cada uma, por um colorizador só, e não um por situação.
         *
         * Continua existindo porque a cor pode ser automática: enquanto houver situação sem
         * cor escolhida, alguém tem de resolver, e este é o lugar. Quem só desenha — os chips,
         * as bolinhas — recebe a lista já resolvida.
         *
         * Método, e não getter: o deepCopy do modal de edição é JSON, e getter vira campo na
         * cópia — iria parar no rascunho que se compara e se envia.
         */
        coloredStates(): Array<State & { color: string }> {
            const colorizer = new StateColorizer(this.states)
            return this.states.map((state: State) => ({...state, color: colorizer.color(state.id)}))
        },

        /**
         * A situação de UM item, já com a cor — a forma em que ela se desenha.
         *
         * Junta as duas perguntas que quem mostra um item sempre faz juntas: "em que situação
         * ele está" (caindo na inicial quando não tem nenhuma) e "de que cor ela é". Era um
         * método privado da listagem, e a paleta de busca seria a segunda cópia.
         */
        coloredStateOf(instance: { _state?: number }): State & { color: string } {
            const state = this.currentStateOf(instance)
            return {...state, color: this.colorOfState(state.id)}
        }
    }
}

export type EnhancedWorkflow = ReturnType<typeof enhanceWorkflow>

export default Workflow
